import classnames from 'classnames';
import React, { FC, useEffect, useState, useId } from 'react';

import { LinkButton } from '@eika/button';
import { CheckmarkList } from '@eika/checkmark-list';
import { IconTypes } from '@eika/icon';

import RadioButton from './RadioButton';
import { RadioButtonCardProps } from './Types';
import { toDataTrackIdFormat } from './utils/toDataTrackIdFormat';

const RadioButtonCard: FC<RadioButtonCardProps> = ({
    value,
    selectedOption,
    onChange,
    label,
    description,
    id,
    describedBy,
    required,
    error,
    inputRef,
    name,
    highlightedNote,
    checkmarks,
    bannerElement,
    expandableLabel,
    expandableCheckmarks,
    uncheckedListLabel,
    dataTrackId,
    dataTrackLinkname = value || label,
    expandCheckmarksOnSelect = false,
    ...rest
}) => {
    const generatedId = useId();
    const optionId = id ?? generatedId;
    const [expanded, setExpanded] = useState(false);
    useEffect(() => {
        if (expandCheckmarksOnSelect && value === selectedOption) setExpanded(true);
    }, [expandCheckmarksOnSelect, value, selectedOption]);

    return (
        <>
            {highlightedNote && (
                <div className="sgw-radio-card-option__highlighted-note">
                    <p>{highlightedNote}</p>
                </div>
            )}
            <div className="sgw-radio-button-panel-option">
                <label
                    className={classnames('sgw-radio-card__wrapper', {
                        'sgw-radio-card__wrapper__error': error,
                        'sgw-radio-card__wrapper__checked': value === selectedOption,
                    })}
                    htmlFor={optionId}
                >
                    <RadioButton
                        {...rest}
                        selectedOption={selectedOption}
                        error={error}
                        aria-describedby={describedBy}
                        required={required}
                        aria-invalid={error}
                        onChange={onChange}
                        name={name}
                        id={optionId}
                        dataTrackId={toDataTrackIdFormat(dataTrackId)}
                        dataTrackLinkname={dataTrackId ? toDataTrackIdFormat(dataTrackLinkname) : undefined}
                        ref={inputRef}
                        value={value}
                        label={label}
                        className="sgw-radio-card-option"
                    />
                </label>
                {description && <span className="sgw-radio-card-option__description">{description}</span>}
                {checkmarks && (
                    <div className="sgw-radio-card-option__checkmark-list__wrapper">
                        <CheckmarkList
                            checkmarks={checkmarks.map((checkmark) => ({
                                label: checkmark,
                                checked: true,
                            }))}
                            uncheckedListLabel={uncheckedListLabel}
                        />
                    </div>
                )}
                {expandableLabel && expandableCheckmarks && (
                    <div className="sgw-radio-button-panel__expandable-checkmarks">
                        <LinkButton
                            className="sgw-radio-button-panel__expand-button"
                            onClick={() => setExpanded(!expanded)}
                            icon={expanded ? IconTypes.LITEN_PIL_OPP : IconTypes.LITEN_PIL_NED}
                            iconOnRightSide
                        >
                            {expandableLabel}
                        </LinkButton>
                        {expanded && (
                            <div className="sgw-radio-card-option__checkmark-list__wrapper">
                                <CheckmarkList checkmarks={expandableCheckmarks} />
                            </div>
                        )}
                    </div>
                )}
                {bannerElement && <div className="sgw-radio-card-option__banner">{bannerElement}</div>}
            </div>
        </>
    );
};

export default RadioButtonCard;
